/**
 * <contact-section> — contact form behaviour:
 *   1. Submit button stays disabled until every required checkbox is ticked.
 *   2. Client-side per-field validation (native Constraint Validation API).
 *      Errors show only after the first submit attempt, then clear live as the
 *      user fixes each field. Each invalid field gets `field--error`, a localized
 *      message (`input.validationMessage`) and a "Clear" button that empties
 *      the field (handled here, scoped to this contact form).
 *
 * Loaded from `sections/section-contact_form.liquid` via
 * `type="module" fetchpriority="low"`.
 *
 * Dependencies:
 *   - @theme/delegate
 */

import { Delegate } from '@theme/delegate';

class ContactSection extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    // Delegate binds a listener on construction (setRoot) — create it here, not
    // in the constructor, so binding happens only once the element is connected.
    this.domDelegate = new Delegate(this);
    this.submitted = false;
    this.clearLabel = this.dataset.clearLabel || '';
    this.onSubmit = this.#onSubmit.bind(this);
    this.onSubmitClick = this.#onSubmitClick.bind(this);

    this.domDelegate.on(
      'change',
      "[data-state='required']",
      this.#toggleButton.bind(this)
    );
    this.domDelegate.on(
      'click',
      "[data-action='clear-field']",
      this.#clearField.bind(this)
    );
    this.domDelegate.on('input', '.input-placeholder', this.#onInput.bind(this));

    // Capture phase so we run BEFORE Shopify's spam-protection/captcha handlers,
    // which otherwise submit the form programmatically (bypassing a bubble-phase
    // listener). Click covers the button, submit covers the Enter key.
    this.addEventListener('click', this.onSubmitClick, true);
    this.addEventListener('submit', this.onSubmit, true);

    this.#toggleButton();
  }

  disconnectedCallback() {
    this.domDelegate.off();
    this.removeEventListener('click', this.onSubmitClick, true);
    this.removeEventListener('submit', this.onSubmit, true);
  }

  #onSubmitClick(event) {
    if (!event.target.closest('.contact-section__submit')) return;
    this.#guardSubmit(event);
  }

  #onSubmit(event) {
    this.#guardSubmit(event);
  }

  #guardSubmit(event) {
    this.submitted = true;

    let firstInvalid = null;
    this.#inputs().forEach((input) => {
      if (!this.#validate(input) && !firstInvalid) firstInvalid = input;
    });

    if (firstInvalid) {
      event.preventDefault();
      event.stopImmediatePropagation();
      firstInvalid.focus();
    }
  }

  #onInput(event) {
    if (!this.submitted) return;
    this.#validate(event.target);
  }

  #inputs() {
    return Array.from(this.querySelectorAll('.input-placeholder'));
  }

  #validate(input) {
    const field = input.closest('.field');
    if (!field) return true;

    const valid = input.checkValidity();
    field.classList.toggle('field--error', !valid);

    if (valid) {
      input.removeAttribute('aria-invalid');
      this.#removeError(field);
    } else {
      input.setAttribute('aria-invalid', 'true');
      this.#showError(field, input);
    }
    return valid;
  }

  #showError(field, input) {
    let message = field.querySelector('.field__error-message');
    let created = false;
    if (!message) {
      message = document.createElement('span');
      message.className = 'field__error-message u-p6';
      message.setAttribute('aria-live', 'polite');
      message.id = `${input.id}-error`;
      field.appendChild(message);
      created = true;
    }
    // Programmatic association so SR re-announces the error when the field regains focus
    input.setAttribute('aria-describedby', message.id);

    if (created) {
      // A polite aria-live region only announces text that changes AFTER the region
      // already exists in the DOM. The span was just created, so defer the text one
      // frame — otherwise 2nd+ invalid fields (which never get focus) stay silent.
      requestAnimationFrame(() => {
        if (message.isConnected) message.textContent = input.validationMessage;
      });
    } else {
      message.textContent = input.validationMessage;
    }

    if (!field.querySelector('.field-clear')) {
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'field-clear u-p3';
      clear.dataset.action = 'clear-field';
      clear.textContent = this.clearLabel;
      field.insertBefore(clear, message);
    }
  }

  #removeError(field) {
    const input = field.querySelector('.input-placeholder');
    input?.removeAttribute('aria-describedby');
    input?.removeAttribute('aria-invalid');
    field.querySelector('.field__error-message')?.remove();
    field.querySelector('.field-clear')?.remove();
  }

  #clearField(event) {
    const field = event.target.closest('.field');
    if (!field) return;

    const input = field.querySelector('.input-placeholder');
    if (input) {
      input.value = '';
      input.focus();
    }
    field.classList.remove('field--error');
    this.#removeError(field);
  }

  #toggleButton() {
    const boxes = this.querySelectorAll("[data-state='required']");
    const allChecked = Array.from(boxes).every((box) => box.checked);

    const btn = this.querySelector('.contact-section__submit');
    if (!btn) return;

    if (allChecked) {
      btn.removeAttribute('disabled');
    } else {
      btn.setAttribute('disabled', 'true');
    }
  }
}

if (!customElements.get('contact-section')) {
  customElements.define('contact-section', ContactSection);
}
